import { Flex, layout, Link, Span, styled } from '@fareye/ui'
import {
  formatMs,
  getValueFromNestedKey,
  isEmpty,
  startCase
} from '@fareye/utils'
import { displayTime } from '../../../utility/function'
import React from 'react'
import VehicleList from './VehicleList'

interface IHeader {
  align?: 'left' | 'right' | 'center'
}

function dateFormatter(val: any) {
  if (typeof val === 'object') {
    return val?.time ? displayTime(val) : '-'
  }
  return val ? displayTime(val) : '-'
}
export const formatters: any = {
  dateFormatter
}
const regex = /[(http(s)?)://(www.)?a-zA-Z0-9@:%._+~#=]{2,256}.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)/

const ColFullHeight = styled((props: any) => (
  <Flex
    p={2}
    justifyContentSpaceEvenly
    column
    {...props}
    style={{
      height: '100%',
      ...props.style
    }}
  >
    {props.children}
  </Flex>
))`
  ${layout}
`
const FullHeight = styled((props: any) => (
  <Flex
    p={2}
    alignItemsCenter
    textCenter
    {...props}
    style={{ height: '100%', ...props.style }}
  >
    {props.children}
  </Flex>
))`
  ${layout}
`

const Header = styled(Span)`
  width: 100%;
  display: block;
  text-transform: uppercase;
  padding: 0 6px 0 6px;
  text-align: ${({ align = 'left' }: IHeader) => align};
`
const ColorSpan = styled(Span)`
  color: ${(props: any) => props.theme.colors[props?.title || 'inherit']};
`

function generateColumnObject(
  column: any,
  { t, links, queryParams = {} }: any
) {
  const { label, key, formatter } = column
  switch (key) {
    case 'arrivalTime':
      return {
        Header: () => <Header data-id={key}>{t(label)}</Header>,
        id: key,
        width: 160,
        Cell: ({ row: { original } }: any) => (
          <ColFullHeight>
            <Span>
              {original?.ata?.time
                ? displayTime(original.ata)
                : original?.eta?.time
                ? displayTime(original.eta)
                : '-'}
            </Span>
          </ColFullHeight>
        )
      }
    case 'oceanArrivalTime':
      return {
        Header: () => <Header data-id={key}>{t(label)}</Header>,
        id: key,
        width: 160,
        Cell: ({ row: { original } }: any) => (
          <ColFullHeight>
            <Span>
              {original?.ata?.time
                ? displayTime(original.ata)
                : original?.sta?.time
                ? displayTime(original.sta)
                : '-'}
            </Span>
          </ColFullHeight>
        )
      }
    case 'shipmentId':
      return {
        Header: () => <Header data-id={key}>{t(label)}</Header>,
        id: key,
        width: 180,
        Cell: ({ row: { original } }: any) => (
          <ColFullHeight>
            <Link
              target="_blank"
              variant="xs"
              to={`${links.shipmentShare}/${original.slug}`}
            >
              {original.lrNo || '-'}
            </Link>
          </ColFullHeight>
        )
      }
    case 'packageId':
      return {
        Header: () => <Header data-id={key}>{t(label)}</Header>,
        id: key,
        width: 180,
        Cell: ({ row: { original } }: any) => (
          <ColFullHeight>
            <Link
              target="_blank"
              variant="xs"
              to={`${links.packageShare}/${original?.packageId}`}
            >
              {original?.packageId || '-'}
            </Link>
          </ColFullHeight>
        )
      }
    case 'route':
      return {
        Header: () => <Header data-id={key}>{t(label)}</Header>,
        id: key,
        width: 120,
        Cell: ({ row: { original } }: any) => (
          <ColFullHeight>
            <Span>
              {original?.attachLocation?.locationName
                ? `${original?.attachLocation?.locationName} - ${original?.detachLocation?.locationName}`
                : original?.attachRoute}
            </Span>
          </ColFullHeight>
        )
      }
    case 'shipmentRoute':
      return {
        Header: () => <Header data-id={key}>{t(label)}</Header>,
        id: key,
        width: 200,
        Cell: ({ row: { original } }: any) => (
          <ColFullHeight>
            <Span>{`${original?.startLocation?.locationName} - ${original?.endLocation?.locationName}`}</Span>
          </ColFullHeight>
        )
      }
    case 'departureTime':
      return {
        Header: () => <Header data-id={key}>{t(label)}</Header>,
        id: key,
        width: 160,
        Cell: ({ row: { original } }: any) => (
          <ColFullHeight>
            <Span>
              {original?.atd?.time
                ? displayTime(original.atd)
                : original?.etd?.time
                ? displayTime(original.etd)
                : '-'}
            </Span>
          </ColFullHeight>
        )
      }
    case 'oceanDepartureTime':
      return {
        Header: () => <Header data-id={key}>{t(label)}</Header>,
        id: key,
        width: 160,
        Cell: ({ row: { original } }: any) => (
          <ColFullHeight>
            <Span>
              {original?.atd?.time
                ? displayTime(original.atd)
                : original?.etd?.time
                ? displayTime(original.etd)
                : '-'}
            </Span>
          </ColFullHeight>
        )
      }
    case 'transshipment':
      return {
        Header: () => <Header data-id={key}>{t(label)}</Header>,
        id: key,
        width: 120,
        Cell: ({ row: { original } }: any) => (
          <ColFullHeight>
            <Span>
              {!isEmpty(original.transshipment)
                ? original.transshipment.join(' , ')
                : t('Direct')}
            </Span>
          </ColFullHeight>
        )
      }

    case 'routeState':
      return {
        Header: () => <Header data-id={'inState | status'}>{t(label)}</Header>,
        id: key,
        sortable: false,
        width: 350,
        Cell: ({ row: { original } }: any) => {
          return (
            <FullHeight alignItemsCenter>
              <ColorSpan
                variant="h6"
                style={{
                  padding: '2px 10px',
                  borderRadius: '6px',
                  marginRight: '4px'
                }}
                title={original?.inState}
              >
                {original?.inState ? t(startCase(original.inState)) : ''}
              </ColorSpan>
              <ColorSpan
                variant="h6"
                title={original?.status}
                style={{ padding: '2px 10px', borderRadius: '6px' }}
              >
                {original?.status ? t(startCase(original.status)) : ''}
              </ColorSpan>
            </FullHeight>
          )
        }
      }
    case 'packageRouteState':
      return {
        Header: () => <Header data-id={'routeState'}>{t(label)}</Header>,
        id: key,
        sortable: false,
        width: 350,
        Cell: ({ row: { original } }: any) => {
          return (
            <FullHeight alignItemsCenter>
              <ColorSpan
                variant="h6"
                style={{
                  padding: '2px 10px',
                  borderRadius: '6px',
                  marginRight: '4px'
                }}
                title={original?.routeState}
              >
                {original?.routeState ? t(startCase(original.routeState)) : ''}
              </ColorSpan>
            </FullHeight>
          )
        }
      }
    case 'eventTimeDiff':
      return {
        Header: () => <Header data-id={key}>{t(label)}</Header>,
        id: key,
        width: 120,
        Cell: ({ row: { original } }: any) => {
          const { eventTime = '', expectedEventTime = '' } = original
          const diff: any =
            eventTime && expectedEventTime
              ? new Date(eventTime).getTime() -
                new Date(expectedEventTime).getTime()
              : ''
          return (
            <ColFullHeight>
              <Span color={diff > 0 ? '#C22727' : '#009D32'}>
                {diff ? formatMs(diff) : '-'}
              </Span>
            </ColFullHeight>
          )
        }
      }
    case 'images':
      return {
        Header: () => <Header data-id={key}>{t(label)}</Header>,
        id: key,
        width: 150,
        Cell: ({ row: { original } }: any) => {
          const photos = Object.keys(original).filter(
            (key: string) =>
              key.toLowerCase().includes('photo') &&
              original[key] !== null &&
              regex.test(original[key])
          )
          return (
            <ColFullHeight>
              {photos.map((key: string) => {
                return (
                  <a
                    href={original[key]}
                    key={key}
                    target="_blank"
                    style={{ fontSize: '12px', marginBottom: '6px' }}
                  >
                    {original[key]
                      ? original[key].substring(
                          original[key].lastIndexOf('/') + 1
                        )
                      : '-'}
                  </a>
                )
              })}
            </ColFullHeight>
          )
        }
      }
    case 'signature':
      return {
        Header: () => <Header data-id={key}>{t(label)}</Header>,
        id: key,
        width: 150,
        Cell: ({ row: { original } }: any) => {
          const url: string = original?.epodSignature || ''
          return (
            <ColFullHeight>
              {regex.test(url) ? (
                <a href={url} target="_blank" style={{ fontSize: '12px' }}>
                  {url?.substring(url.lastIndexOf('/') + 1) || '-'}
                </a>
              ) : (
                <Span>{url}</Span>
              )}
            </ColFullHeight>
          )
        }
      }
    case 'vehicleNo':
      return {
        Header: () => <Header data-id={key}>{t(label)}</Header>,
        id: key,
        minWidth: 120,
        Cell: ({ row: { original } }: any) => (
          <ColFullHeight>
            <Span>
              {original.hasMultiVehicle ? (
                <VehicleList
                  currentVehicle={original?.vehicleNo}
                  vehicles={original?.multiVehicleHistory}
                />
              ) : (
                original.vehicleNo
              )}
            </Span>
          </ColFullHeight>
        )
      }
    case 'itemDimension':
      return {
        Header: () => <Header data-id={key}>{t(label)}</Header>,
        id: key,
        width: 150,
        Cell: ({ row: { original } }: any) => {
          const value = `${original.itemLength || '-'} x ${original.itemWidth ||
            '-'} x ${original.itemHeight || '-'}`
          return (
            <ColFullHeight>
              <Span>{value}</Span>
            </ColFullHeight>
          )
        }
      }

    case 'videoLink':
    case 'imageLink':
      return {
        Header: () => <Header data-id={key}>{t(label)}</Header>,
        id: key,
        width: 180,
        Cell: ({ row: { original } }: any) => {
          const value = original?.[key]
          return (
            <ColFullHeight>
              {regex.test(value) ? (
                <a
                  href={value}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Click here to open"
                  style={{ fontSize: '12px', textDecoration: 'underline', color: '#007bff' }}
                >
                  {key === 'videoLink' ? 'Video' : 'Image'}
                </a>
              ) : (
                <Span>-</Span>
              )}
            </ColFullHeight>
          )
        }
      }

    case 'others':
      return {
        Header: () => <Header data-id={key}>{t(label)}</Header>,
        id: key,
        width: 300,
        Cell: ({ row: { original } }: any) => {
          const value = original?.[key]

          if (!value) {
            return <Span>-</Span>
          }

          // If backend sends object
          if (typeof value === 'object') {
            return (
              <Span
                style={{ fontSize: '12px', whiteSpace: 'pre-wrap' }}
                title={JSON.stringify(value)}
              >
                {JSON.stringify(value)}
              </Span>
            )
          }

          // If backend sends string
          return (
            <Span
              style={{ fontSize: '12px', whiteSpace: 'pre-wrap' }}
              title={String(value)}
            >
              {String(value)}
            </Span>
          )
        },
      }
    
    case 'eventTime':
      return {
        Header: () => <Header data-id={key}>{t(label)}</Header>,
        id: key,
        width: 160,
        Cell: ({ row: { original } }: any) => (
          <ColFullHeight>
            <Span>
            {displayTime(original?.eventTime)}
            </Span>
          </ColFullHeight>
        )
      }

    default:
      return {
        Header: () => <Header data-id={key}>{t(label)}</Header>,
        id: key,
        width: 140,
        Cell: ({ row: { original } }: any) => {
          let value = getValueFromNestedKey(original, key)
          return (
            <ColFullHeight>
              <Span variant="ellipsis">
                {formatter && formatters[formatter]
                  ? formatters[formatter](value) || '-'
                  : (!!value && typeof value === 'string' ? t(value) : value) ||
                    (value === 0 ? 0 : '-')}
              </Span>
            </ColFullHeight>
          )
        }
      }
  }

  
}
export function getTableColumns(
  reqdColumns: any[],
  { t, links, queryParams = {} }: any
) {
  const result = reqdColumns.map(ele =>
    generateColumnObject(ele, { t, links, queryParams })
  )
  return result
}
